import React, { useState, useEffect, useCallback } from "react";
import { GetDashboards, GetDashboard, SaveDashboard, DeleteDashboard, ListKPIs } from "./api";
import { GetParam, UpdateParam, CreateParam } from "aldoo-ra/Admin/ParamIO/api";
import DropDownSelect from "aldoo-ra/DropdownSelect";
import DateFilterDropdown from "aldoo-ra/DateFilterDropdown";
import Modal from "aldoo-ra/Modal";
import Button from "aldoo-ra/Button";
import ToggleSwitch from "aldoo-ra/ToggleSwitch";
import Tooltip from "aldoo-ra/Tooltip";
import Input from "aldoo-ra/Input";
import Typography from "aldoo-ra/Typography";
import Panel from "aldoo-ra/Panel";
import { showPromptDialogAsync } from "aldoo-ra/PromptDialog";
import { showSuccessToast, showToast } from "aldoo-ra/Toast";
import { TrashIcon, PlusIcon, ArrowsPointingOutIcon, ArrowsPointingInIcon } from "@heroicons/react/24/outline";
import WidgetRenderer from "./components/WidgetRenderer";
import useKPIData from "./hooks/useKPIData";


const TIME_RANGE_OPTIONS = ["sync", "30m", "1h", "6h", "24h", "7d", "30d", "90d"].map((v) => ({ value: v, label: v === "sync" ? "Dashboard Sync" : v }));
const VIZ_OPTIONS = ["line", "bar", "number", "gauge", "table", "pie"].map((v) => ({ value: v, label: v }));
const GRANULARITY_OPTIONS = ["second", "minute", "hour", "day"].map((v) => ({ value: v, label: v }));
const AGGREGATION_OPTIONS = ["latest", "avg", "max", "min", "sum", "count"].map((v) => ({ value: v, label: v }));

const EMPTY_WIDGET = {
    kpi: "",
    appId: "",
    visualization: "line",
    timeRange: "sync",
    granularity: "hour",
    aggregation: "avg",
    groupByTag: null,
    live: false,
    position: { x: 0, y: 0, w: 1, h: 1 },
    displayConfig: {},
};

const EMPTY_DASHBOARD = { name: "", description: "", layout: { columns: 2 }, widgets: [] };

const selVal = (sel) => (Array.isArray(sel) ? sel[0]?.value : sel?.value);

const DATE_RANGE_PRESETS = [
    { id: "30m", label: "Last 30 minutes", value: { minutes: 30 } },
    { id: "1h", label: "Last 1 hour", value: { hours: 1 } },
    { id: "6h", label: "Last 6 hours", value: { hours: 6 } },
    { id: "24h", label: "Last 24 hours", value: { hours: 24 } },
    { id: "7d", label: "Last 7 days", value: { days: 7 } },
    { id: "30d", label: "Last 30 days", value: { days: 30 } },
    { id: "90d", label: "Last 90 days", value: { days: 90 } },
    { id: "365d", label: "Last 365 days", value: { days: 365 } },
    { id: "lifetime", label: "Lifetime", value: null },
    { id: "custom", label: "Custom Range", value: "custom" },
];

function resolveTimeRange(filterValue) {
    if (!filterValue || filterValue.type === "custom") return "custom";
    if (filterValue.type !== "preset") return "24h";
    const v = filterValue.value;
    if (!v) return "90d";
    if (v.minutes === 30) return "30m";
    if (v.hours === 1) return "1h";
    if (v.hours === 6) return "6h";
    if (v.hours === 24) return "24h";
    if (v.days === 7) return "7d";
    if (v.days === 30) return "30d";
    if (v.days === 90) return "90d";
    if (v.days === 365) return "90d";
    return "24h";
}

export default function KPIDashboardOverview() {
    const [dashboards, setDashboards] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [dashboard, setDashboard] = useState({ ...EMPTY_DASHBOARD });
    const [globalTimeRange, setGlobalTimeRange] = useState("24h");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [showAddWidget, setShowAddWidget] = useState(false);
    const [newWidget, setNewWidget] = useState({ ...EMPTY_WIDGET });

    const [showSaveModal, setShowSaveModal] = useState(false);
    const [saveName, setSaveName] = useState("");
    const [saveDesc, setSaveDesc] = useState("");

    const [kpiOptions, setKpiOptions] = useState([]);
    const [kpiEnabled, setKpiEnabled] = useState(false);
    const [toggling, setToggling] = useState(false);

    useEffect(() => {
        GetDashboards().then((res) => {
            if (res.success) {
                const list = res.dashboards || [];
                setDashboards(list);
                if (list.length > 0) {
                    // Restore selection from ?dashboard=<id> in URL, fall back to first.
                    const urlId = typeof window !== "undefined"
                        ? new URLSearchParams(window.location.search).get("dashboard")
                        : null;
                    const match = urlId && list.find((d) => d._id === urlId);
                    const initialId = match ? match._id : list[0]._id;
                    setSelectedId(initialId);
                    loadDashboard(initialId);
                    if (!match && typeof window !== "undefined") {
                        // Write the resolved id back so the URL is always stable.
                        const url = new URL(window.location.href);
                        url.searchParams.set("dashboard", initialId);
                        window.history.replaceState({}, "", url);
                    }
                }
            }
            setLoading(false);
        });
        ListKPIs().then((res) => {
            if (res.success) setKpiOptions((res.kpis || []).map((k) => ({ value: k.id, label: `${k.name} (${k.id})` })));
        });
        GetParam("KPI.enabled").then((p) => {
            if (p && p.result !== false) setKpiEnabled(p.value === true || p.value === "true");
        }).catch(() => {});
    }, []);

    const handleToggleEnabled = async (enabled) => {
        setToggling(true);
        try {
            const existing = await GetParam("KPI.enabled").catch(() => null);
            if (existing && existing.result !== false) {
                await UpdateParam({ address: "KPI.enabled", value: enabled, description: "Enable KPI telemetry system" });
            } else {
                await CreateParam({ address: "KPI.enabled", value: enabled, description: "Enable KPI telemetry system" });
            }
            setKpiEnabled(enabled);
            showSuccessToast(enabled ? "KPI system enabled" : "KPI system disabled", "", {
                duration: 3000,
                position: "TOP_CENTER",
                enableDismissalTracking: false,
            });
        } catch (e) {
            showToast({ title: "Failed to toggle KPI", variant: "error", duration: 3000, position: "TOP_CENTER" });
        }
        setToggling(false);
    };

    const loadDashboard = useCallback((id) => {
        if (!id) { setDashboard({ ...EMPTY_DASHBOARD }); return; }
        GetDashboard(id).then((res) => {
            if (res.success) setDashboard(res.dashboard);
        });
    }, []);

    const updateUrlDashboard = (id) => {
        if (typeof window === "undefined") return;
        const url = new URL(window.location.href);
        if (id) url.searchParams.set("dashboard", id);
        else url.searchParams.delete("dashboard");
        window.history.replaceState({}, "", url);
    };

    const handleSelectDashboard = (sel) => {
        const id = selVal(sel);
        setSelectedId(id);
        loadDashboard(id);
        updateUrlDashboard(id);
    };

    const handleDeleteDashboard = async () => {
        if (!selectedId) return;
        const confirmed = await showPromptDialogAsync({
            title: "Delete Dashboard",
            message: "Are you sure you want to delete this dashboard? This cannot be undone.",
            confirmText: "Delete",
            cancelText: "Cancel",
        });
        if (!confirmed) return;
        await DeleteDashboard(selectedId);
        const list = dashboards.filter((d) => d._id !== selectedId);
        setDashboards(list);
        if (list.length > 0) {
            setSelectedId(list[0]._id);
            loadDashboard(list[0]._id);
            updateUrlDashboard(list[0]._id);
        } else {
            setSelectedId(null);
            setDashboard({ ...EMPTY_DASHBOARD });
            updateUrlDashboard(null);
        }
        showSuccessToast("Dashboard deleted", "", {
            duration: 3000,
            position: "TOP_CENTER",
            enableDismissalTracking: false,
        });
    };

    const handleOpenSave = () => {
        setSaveName(dashboard.name || "");
        setSaveDesc(dashboard.description || "");
        setShowSaveModal(true);
    };

    const handleSave = async () => {
        if (!saveName.trim()) return;
        setSaving(true);
        const payload = {
            ...dashboard,
            name: saveName.trim(),
            description: saveDesc.trim(),
            _id: selectedId || undefined,
        };
        const res = await SaveDashboard(payload);
        if (res.success) {
            setDashboard(res.dashboard);
            const list = dashboards.some((d) => d._id === res.dashboard._id)
                ? dashboards.map((d) => (d._id === res.dashboard._id ? { _id: res.dashboard._id, name: res.dashboard.name } : d))
                : [...dashboards, { _id: res.dashboard._id, name: res.dashboard.name }];
            setDashboards(list);
            setSelectedId(res.dashboard._id);
            updateUrlDashboard(res.dashboard._id);
            setShowSaveModal(false);
            showSuccessToast("Dashboard saved", "", {
                duration: 3000,
                position: "TOP_CENTER",
                enableDismissalTracking: false,
            });
        }
        setSaving(false);
    };

    const handleAddWidget = () => {
        if (!newWidget.kpi) return;
        setDashboard((prev) => ({
            ...prev,
            widgets: [...(prev.widgets || []), { ...newWidget }],
        }));
        setShowAddWidget(false);
        setNewWidget({ ...EMPTY_WIDGET });
    };

    const handleRemoveWidget = (index) => {
        setDashboard((prev) => ({
            ...prev,
            widgets: (prev.widgets || []).filter((_, i) => i !== index),
        }));
    };

    const handleUpdateWidget = (index, field, value) => {
        setDashboard((prev) => ({
            ...prev,
            widgets: (prev.widgets || []).map((w, i) =>
                i === index ? { ...w, [field]: value } : w
            ),
        }));
    };

    const handleDateFilterChange = useCallback((filterValue) => {
        setGlobalTimeRange(resolveTimeRange(filterValue));
    }, []);

    const dashOptions = dashboards.map((d) => ({ value: d._id, label: d.name }));
    const selectedLabel = dashOptions.find((d) => d.value === selectedId)?.label || "";
    const widgets = dashboard.widgets || [];
    const columns = dashboard.layout?.columns || 2;

    return (
        <div style={{ padding: "1.5rem" }}>
            <div style={{ display: "flex", gap: "0.75rem", marginBottom: "1.5rem", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "nowrap" }}>
                <div style={{ width: 200 }}>
                    <DateFilterDropdown
                        variant="admin"
                        customPresets={DATE_RANGE_PRESETS}
                        onChange={handleDateFilterChange}
                        showLabel={false}
                        localUserPreferencesKey="kpi.dashboard.timeRange"
                        className="w-full"
                    />
                </div>
                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "nowrap" }}>
                    <div style={{ width: 200 }}>
                        <DropDownSelect
                            variant="admin"
                            options={dashOptions}
                            value={selectedId ? [{ value: selectedId, label: selectedLabel }] : []}
                            onChange={handleSelectDashboard}
                            placeholder={loading ? "Loading..." : "Select dashboard"}
                            disabled={loading || dashboards.length === 0}
                            className="w-full"
                        />
                    </div>
                    <Typography variant="admin_label" style={{ color: "var(--admin_neutral_3, #94a3b8)" }}>
                        {widgets.length} widget{widgets.length !== 1 ? "s" : ""}
                    </Typography>
                    <Button variant="admin_secondary" onClick={() => setShowAddWidget(true)}>
                        <PlusIcon className="w-4 h-4 mr-1" /> Add Widget
                    </Button>
                    <Button variant="admin_primary" onClick={handleOpenSave}>
                        Save
                    </Button>
                    {selectedId && (
                        <Tooltip text="Delete dashboard" position="top">
                            <Button variant="admin_icon" className="text-admin_text_secondary hover:!text-red-500" onClick={handleDeleteDashboard}>
                                <TrashIcon className="w-4 h-4" />
                            </Button>
                        </Tooltip>
                    )}
                    <div style={{ width: 1, height: 24, background: "var(--admin_bordered, #e2e8f0)" }} />
                    <Tooltip text={kpiEnabled ? "KPI telemetry is active — click to disable" : "KPI telemetry is disabled — click to enable"} position="top">
                        <div style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                            <ToggleSwitch variant="admin" isOn={kpiEnabled} onChange={handleToggleEnabled} />
                            <Typography variant="admin_label" style={{ fontSize: "0.75rem", color: kpiEnabled ? "#22c55e" : "var(--admin_neutral_3, #94a3b8)", whiteSpace: "nowrap" }}>
                                {toggling ? "..." : kpiEnabled ? "Telemetry On" : "Telemetry Off"}
                            </Typography>
                        </div>
                    </Tooltip>
                </div>
            </div>

            {dashboard && (
                <div>
                    <div style={{ display: "grid", gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: "1rem" }}>
                        {widgets.map((widget, i) => (
                            <WidgetCard
                                key={widget._id || `${widget.kpi}-${i}`}
                                widget={widget}
                                globalTimeRange={globalTimeRange}
                                onRemove={() => handleRemoveWidget(i)}
                                onUpdate={(field, value) => handleUpdateWidget(i, field, value)}
                            />
                        ))}
                    </div>
                    {widgets.length === 0 && (
                        <div style={{ textAlign: "center", padding: "3rem", color: "var(--admin_neutral_3, #94a3b8)" }}>
                            <Typography variant="admin_txt1">No widgets yet. Add one to get started.</Typography>
                        </div>
                    )}
                </div>
            )}

            {/* Add Widget Modal */}
            <Modal
                isOpen={showAddWidget}
                onClose={() => setShowAddWidget(false)}
                title="Add Widget"
                variant="admin_centered"
                className="w-full md:w-[480px]"
            >
                <div style={{ padding: "1.5rem", display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                    <Typography variant="admin_label">KPI</Typography>
                    <DropDownSelect
                        variant="admin"
                        options={kpiOptions}
                        value={newWidget.kpi ? [{ value: newWidget.kpi, label: kpiOptions.find((o) => o.value === newWidget.kpi)?.label || newWidget.kpi }] : []}
                        onChange={(sel) => setNewWidget({ ...newWidget, kpi: selVal(sel) || "" })}
                        placeholder="Select KPI"
                        searchEnabled
                    />
                    <Typography variant="admin_label">Visualization</Typography>
                    <DropDownSelect variant="admin" options={VIZ_OPTIONS} value={[{ value: newWidget.visualization, label: newWidget.visualization }]} onChange={(sel) => setNewWidget({ ...newWidget, visualization: selVal(sel) || "line" })} />
                    <Typography variant="admin_label">Period</Typography>
                    <DropDownSelect variant="admin" options={TIME_RANGE_OPTIONS} value={[{ value: newWidget.timeRange, label: newWidget.timeRange }]} onChange={(sel) => setNewWidget({ ...newWidget, timeRange: selVal(sel) || "24h" })} />
                    <Typography variant="admin_label">Granularity</Typography>
                    <DropDownSelect variant="admin" options={GRANULARITY_OPTIONS} value={[{ value: newWidget.granularity, label: newWidget.granularity }]} onChange={(sel) => setNewWidget({ ...newWidget, granularity: selVal(sel) || "hour" })} />
                    <Typography variant="admin_label">Aggregation</Typography>
                    <DropDownSelect variant="admin" options={AGGREGATION_OPTIONS} value={[{ value: newWidget.aggregation, label: newWidget.aggregation }]} onChange={(sel) => setNewWidget({ ...newWidget, aggregation: selVal(sel) || "avg" })} />
                    <Input variant="admin" label="Group By Tag" value={newWidget.groupByTag || ""} onChange={(e) => setNewWidget({ ...newWidget, groupByTag: e.target.value || null })} placeholder="e.g. provider (optional)" />
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <ToggleSwitch variant="admin" isOn={newWidget.live} onChange={(v) => setNewWidget({ ...newWidget, live: v })} />
                        <Typography variant="admin_label">Live</Typography>
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end", marginTop: "0.5rem" }}>
                        <Button variant="admin_secondary" onClick={() => setShowAddWidget(false)}>Cancel</Button>
                        <Button variant="admin_primary" onClick={handleAddWidget} disabled={!newWidget.kpi}>Add</Button>
                    </div>
                </div>
            </Modal>

            {/* Save Dashboard Modal */}
            <Modal
                isOpen={showSaveModal}
                onClose={() => setShowSaveModal(false)}
                title="Save Dashboard"
                variant="admin_centered"
                className="w-full md:w-[400px]"
            >
                <div style={{ padding: "1.5rem", display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                    <Input variant="admin" label="Name" value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="My Dashboard" />
                    <Input variant="admin" label="Description" value={saveDesc} onChange={(e) => setSaveDesc(e.target.value)} placeholder="Optional description" />
                    <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end", marginTop: "0.5rem" }}>
                        <Button variant="admin_secondary" onClick={() => setShowSaveModal(false)}>Cancel</Button>
                        <Button variant="admin_primary" onClick={handleSave} disabled={!saveName.trim() || saving} loading={saving}>Save</Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}

function WidgetCard({ widget, globalTimeRange, onRemove, onUpdate }) {
    const [fullscreen, setFullscreen] = useState(false);
    const widgetRange = widget.timeRange === "sync" ? null : widget.timeRange;
    const effectiveTimeRange = widgetRange || globalTimeRange || "24h";

    const { data, loading, error, latestTags } = useKPIData({
        kpi: widget.kpi,
        appId: widget.appId,
        timeRange: effectiveTimeRange,
        granularity: widget.granularity || "hour",
        aggregation: widget.aggregation || "avg",
        tagFilters: widget.tagFilters || {},
        groupByTag: widget.groupByTag || null,
        live: widget.live || false,
    });

    return (
        <>
        <Panel
            variant="admin_bordered_shadow"
            collapsible={false}
            styleOverrides={{
                header: "flex items-center p-3",
                panel: "border rounded-lg overflow-hidden",
                body: "p-0",
            }}
            title={
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <Typography variant="admin_h4" style={{ margin: 0 }}>
                        {widget.displayConfig?.title || widget.kpi}
                    </Typography>
                    {widget.live && (
                        <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", backgroundColor: "#22c55e", animation: "pulse 2s infinite" }} />
                    )}
                </div>
            }
            headerContent={
                <div style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
                    <Tooltip text="Fullscreen" position="top">
                        <Button variant="admin_icon" className="text-admin_text_secondary" onClick={() => setFullscreen(true)}>
                            <ArrowsPointingOutIcon className="w-4 h-4" />
                        </Button>
                    </Tooltip>
                    <Tooltip text="Live" position="top">
                        <ToggleSwitch variant="admin" isOn={widget.live || false} onChange={(v) => onUpdate("live", v)} />
                    </Tooltip>
                    <Tooltip text="Remove" position="top">
                        <Button variant="admin_icon" className="text-admin_text_secondary hover:!text-red-500" onClick={onRemove}>
                            <TrashIcon className="w-4 h-4" />
                        </Button>
                    </Tooltip>
                </div>
            }
        >
            <WidgetRenderer widget={widget} data={data} latestTags={latestTags} loading={loading} error={error} />
            <div style={{ borderTop: "1px solid var(--admin_bordered, #e2e8f0)", padding: "0.5rem 1rem 0.75rem 1rem", display: "flex", gap: "0.75rem", alignItems: "flex-end", flexWrap: "wrap" }}>
                <div style={{ minWidth: 140 }}>
                    <Typography variant="admin_label" style={{ fontSize: "0.65rem", marginBottom: 1, color: "var(--admin_text_secondary, #64748b)" }}>Period</Typography>
                    <DropDownSelect variant="admin" options={TIME_RANGE_OPTIONS} value={[{ value: widget.timeRange || "24h", label: widget.timeRange === "sync" ? "Dashboard Sync" : widget.timeRange || "24h" }]} onChange={(sel) => onUpdate("timeRange", selVal(sel) || "24h")} showCheckbox={false} showSelectionTick enableResetButton={false} />
                </div>
                <div style={{ minWidth: 110 }}>
                    <Typography variant="admin_label" style={{ fontSize: "0.65rem", marginBottom: 1, color: "var(--admin_text_secondary, #64748b)" }}>Granularity</Typography>
                    <DropDownSelect variant="admin" options={GRANULARITY_OPTIONS} value={[{ value: widget.granularity || "hour", label: widget.granularity || "hour" }]} onChange={(sel) => onUpdate("granularity", selVal(sel) || "hour")} showCheckbox={false} showSelectionTick enableResetButton={false} />
                </div>
                <div style={{ minWidth: 110 }}>
                    <Typography variant="admin_label" style={{ fontSize: "0.65rem", marginBottom: 1, color: "var(--admin_text_secondary, #64748b)" }}>Aggregation</Typography>
                    <DropDownSelect variant="admin" options={AGGREGATION_OPTIONS} value={[{ value: widget.aggregation || "avg", label: widget.aggregation || "avg" }]} onChange={(sel) => onUpdate("aggregation", selVal(sel) || "avg")} showCheckbox={false} showSelectionTick enableResetButton={false} />
                </div>
                <div style={{ minWidth: 90 }}>
                    <Typography variant="admin_label" style={{ fontSize: "0.65rem", marginBottom: 1, color: "var(--admin_text_secondary, #64748b)" }}>Viz</Typography>
                    <DropDownSelect variant="admin" options={VIZ_OPTIONS} value={[{ value: widget.visualization || "line", label: widget.visualization || "line" }]} onChange={(sel) => onUpdate("visualization", selVal(sel) || "line")} showCheckbox={false} showSelectionTick enableResetButton={false} />
                </div>
            </div>
        </Panel>
        {fullscreen && (
            <Modal
                isOpen={fullscreen}
                onClose={() => setFullscreen(false)}
                title={widget.displayConfig?.title || widget.kpi}
                variant="admin_fullScreen"
                closeOnEscape
                allowCloseBtn={false}
                styleOverrides={{ contentWrapper: "overflow-hidden" }}
                headerContent={
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        {widget.live && (
                            <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", backgroundColor: "#22c55e", animation: "pulse 2s infinite" }} />
                        )}
                        <Tooltip text="Exit fullscreen" position="bottom">
                            <Button variant="admin_icon" onClick={() => setFullscreen(false)}>
                                <ArrowsPointingInIcon className="w-5 h-5" />
                            </Button>
                        </Tooltip>
                    </div>
                }
                footerContent={
                    <div style={{ display: "flex", gap: "0.75rem", alignItems: "flex-end", flexWrap: "wrap", padding: "0.5rem 0 1rem 1rem" }}>
                        <div style={{ minWidth: 140 }}>
                            <Typography variant="admin_label" style={{ fontSize: "0.65rem", marginBottom: 1, color: "var(--admin_text_secondary, #64748b)" }}>Period</Typography>
                            <DropDownSelect variant="admin" options={TIME_RANGE_OPTIONS} value={[{ value: widget.timeRange || "24h", label: widget.timeRange === "sync" ? "Dashboard Sync" : widget.timeRange || "24h" }]} onChange={(sel) => onUpdate("timeRange", selVal(sel) || "24h")} showCheckbox={false} showSelectionTick enableResetButton={false} />
                        </div>
                        <div style={{ minWidth: 110 }}>
                            <Typography variant="admin_label" style={{ fontSize: "0.65rem", marginBottom: 1, color: "var(--admin_text_secondary, #64748b)" }}>Granularity</Typography>
                            <DropDownSelect variant="admin" options={GRANULARITY_OPTIONS} value={[{ value: widget.granularity || "hour", label: widget.granularity || "hour" }]} onChange={(sel) => onUpdate("granularity", selVal(sel) || "hour")} showCheckbox={false} showSelectionTick enableResetButton={false} />
                        </div>
                        <div style={{ minWidth: 110 }}>
                            <Typography variant="admin_label" style={{ fontSize: "0.65rem", marginBottom: 1, color: "var(--admin_text_secondary, #64748b)" }}>Aggregation</Typography>
                            <DropDownSelect variant="admin" options={AGGREGATION_OPTIONS} value={[{ value: widget.aggregation || "avg", label: widget.aggregation || "avg" }]} onChange={(sel) => onUpdate("aggregation", selVal(sel) || "avg")} showCheckbox={false} showSelectionTick enableResetButton={false} />
                        </div>
                        <div style={{ minWidth: 90 }}>
                            <Typography variant="admin_label" style={{ fontSize: "0.65rem", marginBottom: 1, color: "var(--admin_text_secondary, #64748b)" }}>Viz</Typography>
                            <DropDownSelect variant="admin" options={VIZ_OPTIONS} value={[{ value: widget.visualization || "line", label: widget.visualization || "line" }]} onChange={(sel) => onUpdate("visualization", selVal(sel) || "line")} showCheckbox={false} showSelectionTick enableResetButton={false} />
                        </div>
                    </div>
                }
            >
                <div style={{ height: "calc(100vh - 160px)" }}>
                    <WidgetRenderer widget={widget} data={data} latestTags={latestTags} loading={loading} error={error} fullscreen />
                </div>
            </Modal>
        )}
        </>
    );
}
